import React from "react";
import { t } from "../../../../utils/i18n";
import { formatProductTag } from "../Helper";

/**
 * GoHighLevelFormSection Component
 * Handles the UI and logic for GoHighLevel integration settings.
 */
const GoHighLevelFormSection = ({
  formData,
  handleInputChange,
  handleBlur,
  errors,
  showGhlApiKeyTooltip,
  setShowGhlApiKeyTooltip,
  isEditing,
}) => {
  // When editing, we just show the status of GHL integration but don't allow changes
  if (isEditing) {
    return (
      <>
        <div className="form-group">
          <div
            className="alert alert-warning"
            style={{
              backgroundColor: "#fff3cd",
              borderColor: "#ffeeba",
              color: "#856404",
            }}
          >
            <i className="fas fa-info-circle mr-2"></i>
            <strong>{t("store_integrations.widget.ghl_note")}</strong>{" "}
            {formData.gohighlevel_enabled
              ? t("store_integrations.widget.ghl_enabled_locked")
              : t("store_integrations.widget.ghl_locked")}
            {t("store_integrations.widget.ghl_change_hint")}
          </div>
          {/* Hidden input to satisfy any validation */}
          <input
            type="hidden"
            id="gohighlevel_api_key"
            name="gohighlevel_api_key"
            value="dummy-preserved-key"
          />
        </div>

        {/* Tags Section - still editable in edit mode */}
        {formData.gohighlevel_enabled && (
          <div className="form-group">
            <label>{t("store_integrations.widget.tags")}</label>
            <div className="tags-section">
              <div className="default-tags">
                <h4>{t("store_integrations.widget.default_tags")}</h4>
                <div className="tags-display">
                  <span className="tag">not_paid</span>
                  <span className="tag">paid</span>
                  {formData.product_name && formData.product_name.trim() && (
                    <span className="tag product-name">
                      {formatProductTag(formData.product_name)}
                    </span>
                  )}
                </div>
              </div>
              <div className="custom-tags">
                <h4>{t("store_integrations.widget.custom_tags")}</h4>
                {formData.custom_tags.map((tag, index) => (
                  <div key={index} className="custom-tag-input">
                    <input
                      type="text"
                      name={`custom_tag_${index}`}
                      value={tag}
                      onChange={handleInputChange}
                      placeholder={t("store_integrations.widget.custom_tag", { number: index + 1 })}
                      className="form-input"
                      maxLength={30}
                    />
                  </div>
                ))}
              </div>
              <div className="all-tags-preview">
                <h4>{t("store_integrations.widget.all_tags")}</h4>
                <div className="tags-display">
                  {formData.gohighlevel_tags &&
                    formData.gohighlevel_tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // For new widgets, show the full GHL section
  return (
    <>
      {/* GoHighLevel Enabled Checkbox */}
      <div className="form-group">
        <div className="checkbox-wrapper">
          <input
            type="checkbox"
            id="gohighlevel_enabled"
            name="gohighlevel_enabled"
            checked={formData.gohighlevel_enabled}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="gohighlevel_enabled">
            {t("store_integrations.widget.enable_ghl")}
          </label>
        </div>
      </div>

      {/* GoHighLevel Specific Fields */}
      {formData.gohighlevel_enabled && (
        <>
          <div className="form-group">
            <div className="gohighlevel-api-key-label-container">
              <label htmlFor="gohighlevel_api_key">{t("store_integrations.widget.ghl_key_label")}</label>
              <span
                className="gohighlevel-api-key-tooltip-trigger"
                onMouseEnter={() => setShowGhlApiKeyTooltip(true)}
                onMouseLeave={() => setShowGhlApiKeyTooltip(false)}
                onFocus={() => setShowGhlApiKeyTooltip(true)}
                onBlur={() => setShowGhlApiKeyTooltip(false)}
                role="button"
                tabIndex={0}
                aria-describedby="ghl-api-key-tooltip-instructions"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowGhlApiKeyTooltip(!showGhlApiKeyTooltip);
                  } else if (e.key === "Escape") {
                    setShowGhlApiKeyTooltip(false);
                  }
                }}
              >
                ?
              </span>
              {showGhlApiKeyTooltip && (
                <div
                  id="ghl-api-key-tooltip-instructions"
                  className="gohighlevel-api-key-tooltip-content visible"
                  role="tooltip"
                >
                  <strong>{t("store_integrations.widget.ghl_howto")}</strong>
                  <ul>
                    <li>{t("store_integrations.widget.ghl_step1")}</li>
                    <li dangerouslySetInnerHTML={{ __html: t("store_integrations.widget.ghl_step2_html") }} />
                    <li dangerouslySetInnerHTML={{ __html: t("store_integrations.widget.ghl_step3_html") }} />
                    <li>
                      {t("store_integrations.widget.ghl_step4")}
                    </li>
                    <li dangerouslySetInnerHTML={{ __html: t("store_integrations.widget.ghl_step5_html") }} />
                  </ul>
                </div>
              )}
            </div>
            <input
              type="password"
              id="gohighlevel_api_key"
              name="gohighlevel_api_key"
              value={formData.gohighlevel_api_key}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder={t("store_integrations.widget.ghl_key_placeholder")}
              className={`form-input ${
                errors.gohighlevel_api_key ? "error" : ""
              }`}
              required={formData.gohighlevel_enabled}
              aria-label={t("store_integrations.widget.ghl_key_aria")}
              aria-describedby={
                errors.gohighlevel_api_key ? "ghl-api-key-error" : undefined
              }
            />
            {errors.gohighlevel_api_key && (
              <div className="error-message" id="ghl-api-key-error">
                {errors.gohighlevel_api_key}
              </div>
            )}
          </div>

          {/* Tags Section */}
          <div className="form-group">
            <label>{t("store_integrations.widget.tags")}</label>
            <div className="tags-section">
              <div className="default-tags">
                <h4>{t("store_integrations.widget.default_tags")}</h4>
                <div className="tags-display">
                  <span className="tag">not_paid</span>
                  <span className="tag">paid</span>
                  {formData.product_name && formData.product_name.trim() && (
                    <span className="tag product-name">
                      {formatProductTag(formData.product_name)}
                    </span>
                  )}
                </div>
              </div>
              <div className="custom-tags">
                <h4>{t("store_integrations.widget.custom_tags")}</h4>
                {formData.custom_tags.map((tag, index) => (
                  <div key={index} className="custom-tag-input">
                    <input
                      type="text"
                      name={`custom_tag_${index}`}
                      value={tag}
                      onChange={handleInputChange}
                      placeholder={t("store_integrations.widget.custom_tag", { number: index + 1 })}
                      className="form-input"
                      maxLength={30}
                    />
                  </div>
                ))}
              </div>
              <div className="all-tags-preview">
                <h4>{t("store_integrations.widget.all_tags")}</h4>
                <div className="tags-display">
                  {formData.gohighlevel_tags &&
                    formData.gohighlevel_tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GoHighLevelFormSection;
